import type { SiteBlueprint } from "../../types.js";

export type ModernBusinessHomeInput = {
  blueprint: SiteBlueprint;
  stockImageUrl: string;
};

function escapeHtml(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function firstNonEmpty(...values: Array<string | undefined>): string {
  return values.find((value) => value?.trim())?.trim() ?? "";
}

function phoneHref(value: string): string {
  return String(value || "").replace(/[^\d+]/g, "");
}

export function renderModernBusinessHome({
  blueprint,
  stockImageUrl,
}: ModernBusinessHomeInput): string {
  const businessName = escapeHtml(
    firstNonEmpty(blueprint.businessName, "Your Business"),
  );

  const logoUrl = escapeHtml(
    firstNonEmpty(
      blueprint.logo?.src,
      "/assets/placeholders/your-logo-here.png",
    ),
  );

  const heroHeading = escapeHtml(
    firstNonEmpty(
      blueprint.pageHeading,
      blueprint.title,
      "Professional solutions designed around your goals.",
    ),
  );

  const heroDescription = escapeHtml(
    firstNonEmpty(
      blueprint.description,
      "We provide dependable service, thoughtful solutions, and a straightforward experience from start to finish.",
    ),
  );

  const heroImageUrl = escapeHtml(
    firstNonEmpty(
      blueprint.heroMedia?.src,
      blueprint.images?.find((image) => !image.isLogo)?.src,
      stockImageUrl,
    ),
  );

  const phone = escapeHtml(firstNonEmpty(blueprint.phone, "(555) 555-5555"));

  const email = escapeHtml(
    firstNonEmpty(blueprint.email, "hello@yourbusiness.com"),
  );

  const description = escapeHtml(
    firstNonEmpty(
      blueprint.description,
      "We believe good service starts with understanding what you need, communicating clearly, and delivering work you can feel confident about.",
    ),
  );

  const serviceSection = blueprint.sections.find(
    (section) => section.type === "services",
  );

  const services = (serviceSection?.items ?? [])
    .map((item) => {
      if (typeof item === "string") {
        return {
          title: item,
          description: "",
        };
      }

      return {
        title: item.title || item.text || "Professional Service",
        description: item.description || item.body || "",
      };
    })
    .slice(0, 3);

  return `
    <header class="site-header">
      <div class="container nav-wrap">
        <a class="brand" href="/">
          <img
            src="${logoUrl}"
            alt="${businessName}"
            data-panderr-site-logo="true"
            data-panderr-role="logo"
            data-panderr-component="logo"
          >
        </a>

        <nav class="nav" aria-label="Primary navigation">
          <a href="/">Home</a>
          <a href="/services/">Services</a>
          <a href="/pricing/">Pricing</a>
          <a href="/portfolio/">Portfolio</a>
          <a href="/about/">About</a>
          <a href="/contact/">Contact</a>
        </nav>

        <button
          class="menu-button"
          type="button"
          aria-label="Open navigation"
        >
          ☰
        </button>

        <a
          class="button button-primary desktop-cta"
          href="/contact/"
        >
          Start a project
        </a>
      </div>
    </header>

    <main>
      <section class="hero">
        <div class="container hero-grid">
          <div>
            <p class="eyebrow">
              Professional service · Practical solutions
            </p>

            <h1>
              ${heroHeading}
            </h1>

            <p class="lead">
              ${heroDescription}
            </p>

            <div class="hero-actions">
              <a
                class="button button-primary"
                href="/contact/"
              >
                Get started
              </a>

              <a
                class="button button-secondary"
                href="/services/"
              >
                Explore our services
              </a>
            </div>
          </div>

          <aside class="hero-card">
            <div>
              <img
                class="hero-logo"
                src="${heroImageUrl}"
                alt=""
                data-panderr-role="hero-image"
                data-panderr-component="hero-image"
              >
            </div>

            <div>
              <div class="metric">
                Trusted
              </div>

              <p>
                Professional support, clear communication,
                and thoughtful service from start to finish.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <div class="section-head">
            <div>
              <p class="eyebrow">
                Services
              </p>

              <h2>
                How we can help.
              </h2>
            </div>

            <p>
              Explore our core services and find the right
              solution for your needs.
            </p>
          </div>

          <div class="grid-3">
            ${
              services.length
                ? services
                    .map(
                      (service) => `
                        <article class="card">
                          <div class="icon">✓</div>

                          <h3>
                            ${escapeHtml(service.title)}
                          </h3>

                          <p>
                            ${escapeHtml(
                              service.description ||
                                "Professional support tailored to your goals and needs.",
                            )}
                          </p>
                        </article>
                      `,
                    )
                    .join("")
                : `
                  <article class="card">
                    <div class="icon">✓</div>

                    <h3>
                      Professional Service
                    </h3>

                    <p>
                      Dependable solutions designed around
                      your goals, needs, and priorities.
                    </p>
                  </article>

                  <article class="card">
                    <div class="icon">✓</div>

                    <h3>
                      Personal Support
                    </h3>

                    <p>
                      Clear communication and thoughtful
                      guidance from start to finish.
                    </p>
                  </article>

                  <article class="card">
                    <div class="icon">✓</div>

                    <h3>
                      Practical Solutions
                    </h3>

                    <p>
                      Straightforward recommendations focused
                      on quality and lasting value.
                    </p>
                  </article>
                `
            }
          </div>
        </div>
      </section>

      <section class="section section-soft">
        <div class="container">
          <div class="section-head">
            <div>
              <p class="eyebrow">
                About
              </p>

              <h2>
                Built around good service.
              </h2>
            </div>

            <p>
              ${description}
            </p>
          </div>

          <div class="grid-2">
            <article class="card">
              <div class="metric">
                01
              </div>

              <h3>
                Understand your needs
              </h3>

              <p>
                We start by understanding what you need,
                what matters most, and where you want to go.
              </p>
            </article>

            <article class="card">
              <div class="metric">
                02
              </div>

              <h3>
                Deliver a clear solution
              </h3>

              <p>
                We focus on dependable work, practical
                recommendations, and clear communication.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section class="section section-dark">
        <div class="container">
          <div class="section-head">
            <div>
              <p class="eyebrow">
                Why choose us
              </p>

              <h2>
                Thoughtful work.
                Straightforward service.
              </h2>
            </div>

            <p>
              A professional experience should feel clear,
              dependable, and easy to understand.
            </p>
          </div>

          <div class="grid-3">
            <article class="card">
              <h3>
                Clear communication
              </h3>

              <p>
                Know what to expect and where things stand.
              </p>
            </article>

            <article class="card">
              <h3>
                Professional quality
              </h3>

              <p>
                Thoughtful work with attention to detail.
              </p>
            </article>

            <article class="card">
              <h3>
                Practical guidance
              </h3>

              <p>
                Recommendations focused on what actually
                makes sense for your needs.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <div class="cta">
            <div>
              <p class="eyebrow">
                Ready when you are
              </p>

              <h2>
                Let’s find the right solution.
              </h2>
            </div>

            <a
              class="button button-secondary"
              href="/contact/"
            >
              Contact us
            </a>
          </div>
        </div>
      </section>
    </main>

    <footer
      class="site-footer"
      data-panderr-global-component="footer"
      data-panderr-component="footer"
    >
      <div class="container footer-grid">
        <div>
          <h3 data-panderr-field="site.identity.businessName">
            ${businessName}
          </h3>

          <p
            data-panderr-field="site.identity.description"
            data-panderr-optional-field="true"
          >
            ${description}
          </p>

          <div class="panderr-footer-social">
            <a
              href="#"
              aria-label="Facebook"
              data-panderr-field="site.social.facebook"
              data-panderr-social-link="facebook"
            >Facebook</a>

            <a
              href="#"
              aria-label="Instagram"
              data-panderr-field="site.social.instagram"
              data-panderr-social-link="instagram"
            >Instagram</a>

            <a
              href="#"
              aria-label="LinkedIn"
              data-panderr-field="site.social.linkedin"
              data-panderr-social-link="linkedin"
            >LinkedIn</a>

            <a
              href="#"
              aria-label="YouTube"
              data-panderr-field="site.social.youtube"
              data-panderr-social-link="youtube"
            >YouTube</a>
          </div>
        </div>

        <div>
          <h3>
            Explore
          </h3>

          <a href="/">
            Home
          </a>

          <a href="/services/">
            Services
          </a>

          <a href="/about/">
            About
          </a>
        </div>

        <div>
          <h3>
            Contact
          </h3>

          <div
            data-panderr-field="site.contact.address"
            data-panderr-optional-field="true"
          ></div>

          <div
            data-panderr-field="site.contact.serviceArea"
            data-panderr-optional-field="true"
          ></div>

          <a
            href="tel:${phoneHref(phone)}"
            data-panderr-field="site.contact.phone"
          >
            ${phone}
          </a>

          <a
            href="mailto:${email}"
            data-panderr-field="site.contact.email"
          >
            ${email}
          </a>

          <div
            data-panderr-field="site.contact.hours"
            data-panderr-optional-field="true"
          ></div>
        </div>
      </div>

      <div class="container footer-bottom">
        © ${new Date().getFullYear()}
        <span data-panderr-field="site.identity.businessName">
          ${businessName}
        </span>.
        All rights reserved.
        · Created by
        <a href="https://panderr.com/">
          Panderr
        </a>
      </div>
    </footer>
  `;
}
